import { c as _c } from "react/compiler-runtime";
import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { usePathname, useRouter } from '@/i18n/navigation';
import { downloadMedia } from '@/lib/api';
import useAppLocale from './use-app-locale';
import useTranslatedRoute from './use-translated-route';
type DownloadParams = {
  fileId: string;
  fileName: string;
};
const getStatus = (error: unknown): number | undefined => (error as {
  response?: {
    status?: number;
  };
})?.response?.status;
const downloadFile = async ({
  fileId,
  fileName
}: DownloadParams) => {
  const blob = await downloadMedia(fileId);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  return blob;
};
export const useFileDownload = () => {
  const $ = _c(9);
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    locale
  } = useAppLocale();
  const {
    buildRoute
  } = useTranslatedRoute();
  let t0;
  if ($[0] !== buildRoute) {
    t0 = buildRoute("login");
    $[0] = buildRoute;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const loginRoute = t0;
  let t1;
  if ($[2] !== locale || $[3] !== loginRoute || $[4] !== pathname || $[5] !== router || $[6] !== searchParams || $[7] !== t) {
    t1 = {
      mutationFn: downloadFile,
      onError: (error, variables) => {
        const status = getStatus(error);
        if (status === 401) {
          const queryString = searchParams.toString();
          const next = `/${locale}${queryString ? `${pathname}?${queryString}` : pathname}`;
          router.push(`/${loginRoute}?next=${next}`, {
            locale
          });
          return;
        }
        if (status === 403) {
          toast.error(t("no_permission_to_download"));
          return;
        }
        console.error("Download error:", error);
        const errorMessage = error.message?.toLowerCase();
        let message = t("unable_to_download_file", {
          fileName: variables.fileName
        });
        if (errorMessage?.includes("network") || errorMessage?.includes("fetch")) {
          message = t("network_error_check_connection");
        } else {
          if (errorMessage?.includes("not found") || errorMessage?.includes("404")) {
            message = t("file_not_found");
          } else {
            if (error.message) {
              message = error.message;
            }
          }
        }
        toast.error(message);
      }
    };
    $[2] = locale;
    $[3] = loginRoute;
    $[4] = pathname;
    $[5] = router;
    $[6] = searchParams;
    $[7] = t;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return useMutation(t1);
};
export default useFileDownload;